export const simulateBankLending = (params) => {
    const {
        loanAmount = params.propertyValue,
        annualInterestRate = params.interestRate,
        loanTermYears = params.termYears,
        propertyAppreciationRate = params.propertyGrowthRate,
        sellCostRate = params.sellCostRate,
        taxRate = params.taxRate,
        insuranceRate = params.insuranceRate,
        maintenancePercentOfRent = params.maintenancePercentOfRent,
        monthlyRentRatio = params.monthlyRentRatio
    } = params;

    //console.log(params);
    const termMonths = loanTermYears * 12;
    const monthlyInterestRate = (annualInterestRate / 100) / 12;
    const sellCostRateDecimal = sellCostRate / 100;
    const taxRateMonthly = taxRate / 100 / 12;
    const insuranceRateMonthly = insuranceRate / 100 / 12;
    const maintenanceRate = maintenancePercentOfRent / 100;

    const monthlyPayment = loanAmount * monthlyInterestRate / (1 - Math.pow(1 + monthlyInterestRate, -termMonths));
    let remainingLoan = loanAmount;
    let assetValue = loanAmount;

    const monthlyData = [];
    const yearlyData = [];

    let cumPrincipal = 0;
    let cumInterest = 0;
    let cumExpenses = 0;

    for (let month = 1; month <= termMonths; month++) {
        if (month % 12 === 1 && month > 1) {
            assetValue *= (1 + propertyAppreciationRate / 100);
        }

        const interest = remainingLoan * monthlyInterestRate;
        const principal = monthlyPayment - interest;
        remainingLoan -= principal;
        cumPrincipal += principal;
        cumInterest += interest;

        const tax = assetValue * taxRateMonthly;
        const insurance = assetValue * insuranceRateMonthly;
        const assumedRent = assetValue / monthlyRentRatio;
        const maintenance = assumedRent * maintenanceRate;
        const expenses = tax + insurance + maintenance;
        cumExpenses += expenses;

        const closingCost = assetValue * sellCostRateDecimal;
        const bankShare = remainingLoan / assetValue;
        const borrowerShare = 1 - bankShare;
        const entrepreneurEquity = Math.max(0, assetValue - remainingLoan);
        const investorEquity = Object.is(remainingLoan, -0) ? 0 : remainingLoan;

        const entrepreneurExit = Math.max(0, entrepreneurEquity - closingCost - cumExpenses);
        const investorExit = investorEquity <= 0
            ? investorEquity + cumInterest + cumPrincipal
            : Math.max(0, investorEquity + cumPrincipal + cumInterest - closingCost);


        monthlyData.push({
            month,
            assetValue: +assetValue.toFixed(2),
            monthlyRent: principal + interest,
            "investorShare (%)": +(bankShare * 100).toFixed(4),
            "entrepreneurShare (%)": +(borrowerShare * 100).toFixed(4),
            investorEquity: +investorEquity.toFixed(2),
            entrepreneurEquity: +entrepreneurEquity.toFixed(2),
            investorRentIncome: interest,
            investorNetCashflow: +interest.toFixed(2),
            equityFromExtra: 0,
            offsetEquity: 0,
            totalEquityBuyout: +principal.toFixed(2),
            investorTax: 0,
            investorInsurance: 0,
            investorMaintenance: 0,
            entrepreneurTax: +tax.toFixed(2),
            entrepreneurInsurance: +insurance.toFixed(2),
            entrepreneurMaintenance: +maintenance.toFixed(2),
            entrepreneurPayment: +monthlyPayment.toFixed(2),
            cumulativeNetRentIncome: 0,
            cumulativeInvestorExpenses: 0,
            cumulativeEntrepreneurExpenses: +cumExpenses.toFixed(2),
            cumulativeEquityBuyout: +cumPrincipal.toFixed(2),
            cumulativeRentIncome: +cumInterest.toFixed(2),
            closingCost: +closingCost.toFixed(2),
            investorExitValue: +investorExit.toFixed(2),
            entrepreneurExitValue: +entrepreneurExit.toFixed(2),
            reinvestedCapital: 0,
            nonReinvestedCash: 0,
            entrepreneurROI: 0,
            reinvestmentPool: 0,
            goldPurchased: 0,
            totalGoldGrams: 0,
            goldValueAtExit: 0,
            goldPrice: 0,
            cumInterest,
            cumPrincipal
        });
    }

    const final = monthlyData.at(-1) ?? {};
    let cumTax = 0;
    let cumIns = 0;
    let cumMaint = 0;
    for (let i = 0; i < termMonths; i += 12) {
        const chunk = monthlyData.slice(i, i + 12);
        const last = chunk.at(-1) ?? final;
       

        const tax = chunk.reduce((sum, m) => sum + m.entrepreneurTax, 0);
        const ins = chunk.reduce((sum, m) => sum + m.entrepreneurInsurance, 0);
        const maint = chunk.reduce((sum, m) => sum + m.entrepreneurMaintenance, 0);

        cumTax += tax;
        cumIns += ins;
        cumMaint += maint;


        yearlyData.push({
            year: (i / 12) + 1,
            assetValue: last.assetValue ?? 0,
            investorEquity: last.investorEquity ?? 0,
            entrepreneurEquity: last.entrepreneurEquity ?? 0,
            principalPaid: last.cumPrincipal ?? 0,
            interestPaid: last.cumInterest ?? 0,
            investorExitValue: last.investorExitValue ?? 0,
            entrepreneurExitValue: last.entrepreneurExitValue ?? 0,
            remainingLoan: loanAmount - last.cumPrincipal,
            investorNet: chunk.reduce((sum, m) => sum + m.investorNetCashflow, 0),
            equityBuyout: chunk.reduce((sum, m) => sum + m.totalEquityBuyout, 0),
            entrepreneurPaid: chunk.reduce((sum, m) => sum + m.entrepreneurPayment, 0),
            totalPaidSoFar: last.cumPrincipal + last.cumInterest + cumTax + cumIns + cumMaint,
            entrepreneurTax: tax,
            entrepreneurInsurance: ins,
            entrepreneurMaintenance: maint,
            taxPaid: +cumTax ?? 0,
            insurancePaid: +cumIns ?? 0,
            maintenancePaid: +cumMaint ?? 0
        });
    }

    const totalROI = ((final.cumInterest) / loanAmount);
    const annualizedROI = (Math.pow(1 + totalROI, 1 / (final.month / 12)) - 1) * 100;


    const investorResult = {
        model: "Bank Lending",
        originalLoanAmount: loanAmount,
        finalValue: final.assetValue ?? 0,
        capital: loanAmount,
        principal: +cumPrincipal.toFixed(2),
        interest: +cumInterest.toFixed(2),
        nonReinvestedCash: 0,
        exitValue: +final.investorExitValue,
        netCashflow: +cumInterest.toFixed(2),
        totalExpenses: 0,
        totalROI: +(((final.cumInterest) / loanAmount) * 100).toFixed(2),
        annualizedROI: annualizedROI.toFixed(2),
        irrEstimate: annualizedROI.toFixed(2),
        annualNOI: 0,
        capRate: 0,
        cashOnCashReturn: 0,
        termMonths,
        termYears: loanTermYears,
        totalRentCollected: 0
    };

    const borrowerResult = {
        model: "Bank Lending",
        equityBuilt: final.entrepreneurEquity ?? 0,
        exitValue: final.entrepreneurExitValue ?? 0,
        totalRentPaid: 0,
        totalExpenses: final.cumulativeEntrepreneurExpenses ?? 0,
        totalBuyout: final.cumulativeEquityBuyout ?? 0,
        totalPayment: +(final.cumulativeEquityBuyout + final.cumulativeEntrepreneurExpenses + final.cumInterest).toFixed(2),
        monthlyRentPayment: 0,
        monthlyPayment: +monthlyPayment.toFixed(2),
        entrepreneurROI: 0,
        termMonths,
        termYears: loanTermYears,
        principal: final.cumPrincipal,
        interest: final.cumInterest
    };

    console.log('Bank Lending',monthlyData);

    return {
        monthlyData,
        yearlyData,
        investorResult,
        borrowerResult
    };
};
